import { useReactiveVar } from '@apollo/client';
import { capitalize } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useUserContext } from '../../contexts';
import { showErrorFlash, showSuccessFlash } from '../Flash';
import { identifyUser, trackOrderCompleted } from '../../lib/analytics';
import { captureUtmParams, getAttributionData } from '../../lib/attribution';
import { claimHostedPurchase } from '../../lib/hostedPurchase';
import { isStaffAnalyticsSession } from '../../lib/staffAnalytics';
import Spinner from '../Spinner';
import { PLAN_ACTIVATION_DATA_LOCALSTORAGE_KEY } from './constants';
import { activatePlanFromCheckout, buildActivatePlanFromCheckoutVariables } from './mutations';
import { retrieveCheckout } from './queries';
import { PlanActivationStoredData } from './types';
import { activatePlanData } from './vars';

function PlanActivator({ children }: { children: React.ReactNode }) {
  const { refetch: refetchUser, user, isLoggedIn } = useUserContext();
  const userId = user?.id;
  const planDataString =
    useReactiveVar(activatePlanData) ?? localStorage.getItem(PLAN_ACTIVATION_DATA_LOCALSTORAGE_KEY);
  const { checkoutSessionId } = JSON.parse(planDataString || '{}') as Partial<PlanActivationStoredData>;
  const [isActivatingPlan, setIsActivatingPlan] = useState(false);
  const history = useHistory();

  useEffect(() => {
    async function activate() {
      if (isLoggedIn && checkoutSessionId && userId) {
        try {
          setIsActivatingPlan(true);
          if (!isStaffAnalyticsSession()) captureUtmParams();
          const { data } = await activatePlanFromCheckout(
            buildActivatePlanFromCheckoutVariables(checkoutSessionId),
          );
          if (!data) throw new Error('The plan could not be activated. Please try again.');

          await refetchUser?.();
          const { year, plan } = data.activatePlanFromCheckout;

          history.push(plan === 'VIP' ? '/getting-started-with-vip' : '/home');
          const planName = plan === 'VIP' ? 'VIP' : capitalize(plan);
          showSuccessFlash(`Your ${planName} plan has been activated for ${year}`, {
            sticky: true,
          });
          // A slow or failed analytics lookup must not delay activation cleanup.
          void (async () => {
            if (isStaffAnalyticsSession()) return;
            try {
              const { data: checkout } = await retrieveCheckout(checkoutSessionId);
              const paidCheckout = checkout.retrieveCheckout;
              const orderId = paidCheckout?.paymentIntentId || paidCheckout?.invoiceId;
              if (
                isStaffAnalyticsSession() ||
                !paidCheckout?.paymentCompleted ||
                paidCheckout.userId !== userId ||
                !orderId
              ) {
                return;
              }

              if (!claimHostedPurchase(checkoutSessionId, orderId)) return;

              identifyUser(String(userId), {
                email: paidCheckout.email,
                ...getAttributionData(),
              });
              trackOrderCompleted({
                userId,
                invoiceId: orderId,
                checkoutSessionId,
                amountPaid: paidCheckout.amountTotal,
                checkoutSource: 'stripe_checkout',
                analyticsProducts: [
                  {
                    name: paidCheckout.plan,
                    product_id: paidCheckout.plan,
                    price: paidCheckout.amountTotal,
                    quantity: 1,
                  },
                ],
              });
            } catch {
              // Activation already succeeded; purchase matching is best-effort.
            }
          })();
        } catch {
          showErrorFlash(
            'There was an error activating your plan. Please follow the link in your confirmation email.',
          );
        } finally {
          setIsActivatingPlan(false);
          activatePlanData(null);
          localStorage.removeItem(PLAN_ACTIVATION_DATA_LOCALSTORAGE_KEY);
        }
      }
    }

    activate();
  }, [isLoggedIn, checkoutSessionId, refetchUser, history, userId]);

  if (isActivatingPlan) {
    return (
      <div className="flex flex-grow justify-center flex-col items-center pb-8">
        <Spinner className="mt-12" />
      </div>
    );
  }

  return <>{children}</>;
}

export default React.memo(PlanActivator);
